import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Info } from "lucide-react";

const CLASSE = {
  A: "bg-rose-950/50 text-rose-300 border-rose-700/60",
  B: "bg-amber-950/50 text-amber-300 border-amber-700/60",
  C: "bg-sky-950/50 text-sky-300 border-sky-700/60",
};

export default function CurvaABC() {
  const navigate = useNavigate();
  const { canFinance } = useAuth();
  const [data, setData] = useState(null);
  const [criterio, setCriterio] = useState("giro");
  const [periodo, setPeriodo] = useState("180");
  const [categoria, setCategoria] = useState("all");
  const [machine, setMachine] = useState("all");
  const [cats, setCats] = useState([]);
  const [machines, setMachines] = useState([]);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); api.get("/machines").then((r) => setMachines(r.data)); }, []);
  useEffect(() => {
    setData(null);
    const params = { criterio, periodo };
    if (categoria !== "all") params.categoria_id = categoria;
    if (machine !== "all") params.machine_id = machine;
    api.get("/analytics/abc", { params }).then((r) => setData(r.data));
  }, [criterio, periodo, categoria, machine]);

  const sel = "bg-slate-950 border-slate-700 h-9";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-50 flex items-center gap-2"><BarChart3 className="h-7 w-7 text-sky-400" /> Curva ABC</h1>
        <p className="text-slate-400 text-sm">Priorização do estoque por classe A, B e C.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={criterio} onValueChange={setCriterio}>
          <SelectTrigger data-testid="abc-criterio" className={`${sel} w-44`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            <SelectItem value="giro">Critério: Giro (consumo)</SelectItem>
            {canFinance && <SelectItem value="valor">Critério: Valor financeiro</SelectItem>}
          </SelectContent>
        </Select>
        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className={`${sel} w-36`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            <SelectItem value="90">Últimos 90 dias</SelectItem><SelectItem value="180">Últimos 180 dias</SelectItem><SelectItem value="365">Últimos 365 dias</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoria} onValueChange={setCategoria}>
          <SelectTrigger className={`${sel} w-52`}><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-72"><SelectItem value="all">Todas as categorias</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={machine} onValueChange={setMachine}>
          <SelectTrigger className={`${sel} w-52`}><SelectValue placeholder="Máquina" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-72"><SelectItem value="all">Todas as máquinas</SelectItem>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {data === null ? <Skeleton className="h-96 bg-slate-800" /> : (
        <>
          <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3 text-xs text-slate-400 flex items-start gap-2"><Info className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" /> {data.metodologia}</div>
          <div className="grid grid-cols-3 gap-3">
            {["A", "B", "C"].map((c) => (
              <Card key={c} className={`card-metal p-4 border ${CLASSE[c]}`}><div className="text-xs uppercase font-mono">Classe {c}</div><div className="font-display text-3xl font-bold">{data.resumo_classes[c] || 0}</div><div className="text-xs opacity-70">peças</div></Card>
            ))}
          </div>
          <Card className="card-metal p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
                  <th className="text-center px-3 py-3">Classe</th><th className="text-left px-3 py-3">Peça</th><th className="text-left px-3 py-3">Categoria</th>
                  <th className="text-right px-3 py-3">Consumo</th>{data.finance && <th className="text-right px-3 py-3">Valor</th>}<th className="text-right px-3 py-3">% acum.</th>
                </tr></thead>
                <tbody>
                  {data.itens.map((r) => (
                    <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-900/40 cursor-pointer" onClick={() => navigate(`/pecas/${r.id}`)}>
                      <td className="px-3 py-2 text-center"><span className={`rounded border px-2 py-0.5 text-xs font-bold ${CLASSE[r.classe]}`}>{r.classe}</span></td>
                      <td className="px-3 py-2 text-slate-100">{r.descricao}<div className="font-mono text-xs text-sky-400">{r.codigo_interno}</div></td>
                      <td className="px-3 py-2 text-slate-400">{r.categoria}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-200">{num(r.consumo)}</td>
                      {data.finance && <td className="px-3 py-2 text-right font-mono text-emerald-300">{brl(r.metric)}</td>}
                      <td className="px-3 py-2 text-right font-mono text-slate-400">{r.percentual_acumulado}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
